/**
 * RedemptionCard 统一卡片组件
 *
 * 统一展示VIP优惠券和VIP储值卡的卡片组件，UI样式一致，通过数据类型区分取值逻辑
 *
 * Requirements:
 * - 5.1: 新到账角标展示（24小时内）
 * - 5.2: 满减券显示"¥x"格式
 * - 5.3: 折扣券显示"x折"格式
 * - 5.4: 有门槛显示"满x元可用"
 * - 5.5: 无门槛显示"无门槛"
 * - 5.6: 储值卡显示"xxx元储值卡，余额：yyy"格式
 * - 6.1, 6.2: 倒计时展示
 */

import React, { useMemo } from "react";
import {
  CardItemType,
  CouponType,
  RedemptionCardProps,
} from "../../types/coupon-redemption";
import { formatAmount, getThresholdDesc } from "../../utils/coupon-redemption";
import CountdownTimer from "../countdown-timer";
import "./index.less";

/**
 * 统一卡片组件
 *
 * @param props 组件属性
 */
const RedemptionCard: React.FC<RedemptionCardProps> = ({
  item,
  showNewBadge = false,
  showCountdown = false,
  aggregateCount,
  onClick,
}) => {
  const isCoupon = item.itemType === CardItemType.Coupon;
  const isStoredCard = item.itemType === CardItemType.StoredCard;

  // 获取过期时间戳（已经是毫秒时间戳，无需转换）
  const expireTimestamp = item.expireTime;

  // 获取门槛描述
  const thresholdDesc = useMemo(() => {
    return getThresholdDesc(item);
  }, [item]);

  // 渲染优惠券金额区域
  const renderCouponAmount = () => {
    if (!isCoupon) return null;

    // 满减券
    if (item.couponType === CouponType.Discount) {
      return (
        <>
          <span>¥</span>
          <span>{formatAmount(item.couponFee ?? 0)}</span>
        </>
      );
    }

    // 折扣券
    return (
      <>
        <span>{item.couponFee ?? 0}</span>
        <span className="small-amt">折</span>
      </>
    );
  };

  // 渲染储值卡金额区域
  const renderStoredCardAmount = () => {
    if (!isStoredCard) return null;

    return (
      <>
        <span>¥</span>
        <span>{item.chargeCardValidAmt ?? 0}</span>
      </>
    );
  };

  return (
    <div
      className={`redemption-card ${isCoupon ? "coupon-card" : "stored-card"}`}
      onClick={onClick}
    >
      {/* 顶部区域：角标和倒计时 */}
      <div className="card-top">
        {/* 新到账/倒计时角标 */}
        {(showNewBadge || showCountdown) && (
          <div className="badge-container">
            <div className="badge">
              <div className="badge-main">
                {showNewBadge && "新"}
                {showCountdown && (
                  <CountdownTimer expireTime={expireTimestamp} />
                )}
              </div>
              <div className="badge-tail" />
            </div>
          </div>
        )}
      </div>

      {/* 卡片主体内容 */}
      <div className="card-content">
        {/* 储值卡图标 */}
        {isStoredCard && (
          <div className="stored-card-icon">
            <img
              src="https://s.xiaopeng.com/xp-charge/charge-par/charge-vip/redemption-card-icon.svg"
              alt="card-icon"
            />
          </div>
        )}

        {/* 金额区域 */}
        <div className="amount-section">
          {isCoupon && renderCouponAmount()}
          {isStoredCard && renderStoredCardAmount()}
        </div>

        {/* 储值卡：分隔符 */}
        {isStoredCard && (
          <>
            <span className="balance-label">余额</span>
            <img
              className="divider"
              src="https://s.xiaopeng.com/xp-charge/charge-par/charge-vip/redemption-card-divider-light.svg"
              alt="divider"
            />
          </>
        )}

        {/* 门槛描述 / 储值卡名称 */}
        <div className={`threshold-desc ${isStoredCard ? "stored-desc" : ""}`}>
          {thresholdDesc}
        </div>

        {/* 聚合数量分隔线和数量 */}
        {aggregateCount && aggregateCount > 1 && (
          <>
            <img
              className="divider"
              src="https://s.xiaopeng.com/xp-charge/charge-par/charge-vip/redemption-card-divider-light.svg"
              alt="divider"
            />
            <div className="aggregate-count">{aggregateCount}张</div>
          </>
        )}
      </div>
    </div>
  );
};

export default RedemptionCard;
